/**
 * Stage signature functions for comparing stage outputs.
 *
 * Each function extracts the key fields that downstream stages depend on,
 * sorts for order-invariance, and returns a JSON string suitable for
 * equality comparison.  When a stage is re-completed the caller can
 * compare the previous signature with the new one to decide whether
 * downstream data should be cleared.
 */

// ---------------------------------------------------------------------------
// Stage 0 – Questions
// Downstream (MFR) depends on question IDs and answer text.
// ---------------------------------------------------------------------------
export const normalizeStage0Signature = (
  questions: any[],
  answers: Record<string, string>,
): string => {
  const items = (questions || []).map((q: any) => ({
    id: String(q.id || ''),
    answer: (answers[q.id] || '').trim(),
  }));
  items.sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(items);
};

// ---------------------------------------------------------------------------
// Stage 1 – MFR
// Downstream (Product Discovery) depends on MFR raw text.
// ---------------------------------------------------------------------------
export const normalizeStage1Signature = (mfr: any | null): string => {
  return JSON.stringify({ rawText: (mfr?.rawText || '').trim() });
};

// ---------------------------------------------------------------------------
// Stage 2 – Products
// Downstream (PSC) depends on product identity.
// ---------------------------------------------------------------------------
export const normalizeStage2Signature = (products: any[]): string => {
  const items = (products || []).map((p: any) => ({
    product_name: (p.product_name || p.name || '').toString(),
    manufacturer: (p.manufacturer || '').toString(),
    model: (p.model || '').toString(),
  }));
  items.sort((a, b) => {
    const ka = `${a.product_name}|${a.manufacturer}|${a.model}`.toLowerCase();
    const kb = `${b.product_name}|${b.manufacturer}|${b.model}`.toLowerCase();
    return ka.localeCompare(kb);
  });
  return JSON.stringify(items);
};

// ---------------------------------------------------------------------------
// Stage 3 – PSC  (helpers)
// ---------------------------------------------------------------------------

/** Normalize a PSC codes array to sorted plain strings (code only). */
export const pscCodesToStrings = (psc: any): string[] => {
  if (!Array.isArray(psc)) return [];
  const arr = psc.map((item: any) => {
    const raw = typeof item === 'object' && item !== null ? (item.code ?? '') : String(item ?? '');
    return raw.includes(':') ? raw.split(':')[0].trim() : raw.trim();
  });
  arr.sort((a, b) => a.localeCompare(b));
  return arr;
};

// ---------------------------------------------------------------------------
// Stage 3 – PSC  (signature)
// Downstream (NAICS) depends on product identity + PSC codes.
// ---------------------------------------------------------------------------
export const normalizeStage4Signature = (arr: any[]): string => {
  const items = (arr || []).map((p: any) => ({
    product_name: (p.product_name || p.name || '').toString(),
    manufacturer: (p.manufacturer || '').toString(),
    model: (p.model || '').toString(),
    psc_codes: pscCodesToStrings(p.psc_codes),
  }));
  items.sort((a, b) => {
    const ka = `${a.product_name}|${a.manufacturer}|${a.model}`.toLowerCase();
    const kb = `${b.product_name}|${b.manufacturer}|${b.model}`.toLowerCase();
    return ka.localeCompare(kb);
  });
  return JSON.stringify(items);
};

// ---------------------------------------------------------------------------
// Stage 4 – NAICS
// Downstream (Vendors) depends on product identity + NAICS codes.
// ---------------------------------------------------------------------------
export const normalizeStage5Signature = (products: any[]): string => {
  const items = (products || []).map((p: any) => ({
    product_name: (p.product_name || p.name || '').toString(),
    manufacturer: (p.manufacturer || '').toString(),
    model: (p.model || '').toString(),
    naics_from_psc: [...(p.naics_from_psc || [])].sort(),
    naicsCode: (p.naicsCode || '').toString(),
  }));
  items.sort((a, b) => {
    const ka = `${a.product_name}|${a.manufacturer}|${a.model}`.toLowerCase();
    const kb = `${b.product_name}|${b.manufacturer}|${b.model}`.toLowerCase();
    return ka.localeCompare(kb);
  });
  return JSON.stringify(items);
};

// ---------------------------------------------------------------------------
// Stage 5 – Vendors
// Downstream (Section B) depends on product identity + vendor names.
// ---------------------------------------------------------------------------
export const normalizeStage6Signature = (products: any[]): string => {
  const items = (products || []).map((p: any) => ({
    product_name: (p.product_name || p.name || '').toString(),
    manufacturer: (p.manufacturer || '').toString(),
    model: (p.model || '').toString(),
    vendors: [...(p.vendors || [])].map(String).sort(),
  }));
  items.sort((a, b) => {
    const ka = `${a.product_name}|${a.manufacturer}|${a.model}`.toLowerCase();
    const kb = `${b.product_name}|${b.manufacturer}|${b.model}`.toLowerCase();
    return ka.localeCompare(kb);
  });
  return JSON.stringify(items);
};
